import { useState, useEffect } from "react";
import { slaService } from "@/services/slaService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Clock, Save } from "lucide-react";

const PRIORITIES = ["critical", "high", "medium", "low"];

export default function SLASettingsPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    slaService.getConfigs().then(({ data }) => setConfigs(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateConfig = (priority, field, value) => {
    setConfigs((prev) => prev.map((c) => c.priority === priority ? { ...c, [field]: parseInt(value, 10) || 0 } : c));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const config of configs) {
        await slaService.updateConfig(config);
      }
      toast.success("SLA settings saved");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-zinc-200 rounded-sm" /><div className="h-48 bg-zinc-200 rounded-sm" /></div></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="sla-settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]">SLA Settings</h1>
          <p className="text-sm text-[#52525B] mt-0.5">Configure response and resolution time targets per priority</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white gap-1.5" data-testid="save-sla-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-3">
        {PRIORITIES.map((priority) => {
          const config = configs.find((c) => c.priority === priority);
          if (!config) return null;
          return <SLAPriorityCard key={priority} priority={priority} config={config} onChange={updateConfig} />;
        })}
      </div>
    </div>
  );
}

function SLAPriorityCard({ priority, config, onChange }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid={`sla-${priority}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 w-28">
            <Clock size={15} className="text-[#52525B]" />
            <span className="text-sm font-semibold capitalize text-[#09090B]">{priority}</span>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#52525B]">Response (hours)</label>
              <Input type="number" min="0" value={config.response_hours} onChange={(e) => onChange(priority, "response_hours", e.target.value)} className="rounded-sm h-8 text-sm" data-testid={`sla-${priority}-response`} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#52525B]">Resolution (hours)</label>
              <Input type="number" min="0" value={config.resolution_hours} onChange={(e) => onChange(priority, "resolution_hours", e.target.value)} className="rounded-sm h-8 text-sm" data-testid={`sla-${priority}-resolution`} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
